import { useEffect, useRef, useState } from 'react'
import { Avatar } from '../components/Avatar'
import { Auslosung } from '../components/Auslosung'
import { ansageTonUrl, fetchAnsage, type Ansage } from '../lib/api'

/**
 * `/ansage/<id>` (Flo, 31.10.): K4ia steht da, man tippt sie an, sie sagt
 * genau den Text aus dem Admin — gesprochen, nie angezeigt (Flo, 31.10.:
 * „der gesprochene Text soll nicht angezeigt werden“). Der Tipp ist nötig — ohne Geste spielt kein
 * Browser Ton —, und er ist zugleich die Bedienung: noch einmal tippen heißt
 * noch einmal sagen, tippen während sie spricht heißt stopp. Hat die Ansage
 * eine Auslosung, kommt sie nach dem Text: die Trommel (components/Auslosung),
 * dann der Satz dazu, falls einer hinterlegt ist.
 */
export function AnsagePage({ id }: { id: string }) {
  const [ansage, setAnsage] = useState<Ansage | null>(null)
  const [zustand, setZustand] = useState<
    'laedt' | 'bereit' | 'laedtTon' | 'spricht' | 'auslosung' | 'tipp' | 'ergebnis' | 'fehlt'
  >('laedt')
  /** Zählt je Durchlauf hoch, damit eine neue Auslosung von vorn trommelt. */
  const [lauf, setLauf] = useState(0)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const audio = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    let aktiv = true
    fetchAnsage(id)
      .then((a) => {
        if (!aktiv) return
        setAnsage(a)
        setZustand('bereit')
      })
      .catch(() => {
        if (aktiv) setZustand('fehlt')
      })
    return () => {
      aktiv = false
      audio.current?.pause()
    }
  }, [id])

  /** Nach dem Text: die Auslosung, falls es eine gibt — sonst wieder bereit. */
  function nachDemText() {
    if (ansage?.tipp) {
      setLauf((n) => n + 1)
      setZustand('auslosung')
    } else {
      setZustand('bereit')
    }
  }

  /** Die Tonspur holen und abspielen; `danach` läuft, wenn sie zu Ende ist oder es keine gibt. */
  async function abspielen(url: string, danach: () => void): Promise<boolean> {
    try {
      const res = await fetch(url, { cache: 'no-store' })
      if (res.status === 404) {
        danach()
        return false
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; hinweis?: string }
        setHinweis([body.error ?? `HTTP ${res.status}`, body.hinweis].filter(Boolean).join(' '))
        danach()
        return false
      }
      const blob = await res.blob()
      const el = audio.current ?? new Audio()
      audio.current = el
      el.onended = danach
      el.onerror = danach
      el.src = URL.createObjectURL(blob)
      await el.play()
      return true
    } catch (e) {
      setHinweis(e instanceof Error ? e.message : 'Die Tonspur ließ sich nicht abspielen.')
      danach()
      return false
    }
  }

  async function antippen() {
    if (zustand === 'spricht' || zustand === 'tipp') {
      audio.current?.pause()
      setZustand('bereit')
      return
    }
    // „ergebnis“: Die Auslosung steht — antippen heißt noch einmal von vorn.
    if (zustand !== 'bereit' && zustand !== 'ergebnis') return
    setHinweis(null)
    if (ansage && !ansage.sprachausgabe) {
      // Ohne Stimme: Der Text steht ohnehin, also gleich die Auslosung.
      nachDemText()
      return
    }
    setZustand('laedtTon')
    if (await abspielen(ansageTonUrl(id), nachDemText)) setZustand('spricht')
  }

  /** Die Trommel ist stehen geblieben: den Satz dazu sprechen, falls hinterlegt. */
  async function nachDerAuslosung() {
    if (!ansage?.tipp?.text.trim() || !ansage.sprachausgabe) {
      setZustand('ergebnis')
      return
    }
    setZustand('tipp')
    if (!(await abspielen(ansageTonUrl(id, 'tipp'), () => setZustand('ergebnis')))) {
      setZustand('ergebnis')
    }
  }

  if (zustand === 'fehlt') {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-bg p-12 text-center">
        <Avatar state="idle" size={160} showLabel={false} />
        <p className="text-lg text-text-muted">Diese Ansage gibt es nicht mehr.</p>
      </main>
    )
  }

  const spricht = zustand === 'spricht' || zustand === 'tipp'
  const auslosung =
    ansage?.tipp && (zustand === 'auslosung' || zustand === 'tipp' || zustand === 'ergebnis')
  return (
    <main
      className="flex min-h-screen flex-col items-center justify-center gap-8 bg-bg p-8 text-center"
      data-testid="ansage"
      data-zustand={zustand}
    >
      <button
        type="button"
        onClick={() => void antippen()}
        disabled={zustand === 'laedt' || zustand === 'laedtTon'}
        aria-label={spricht ? 'K4ia unterbrechen' : 'K4ia sprechen lassen'}
        className="rounded-full outline-none focus-visible:ring-4 focus-visible:ring-brand/40 disabled:opacity-60"
      >
        <Avatar
          state={spricht ? 'speaking' : zustand === 'laedtTon' ? 'thinking' : 'idle'}
          size={auslosung ? 120 : 220}
          showLabel={false}
        />
      </button>
      {auslosung && ansage?.tipp && (
        <Auslosung
          key={lauf}
          teamA={ansage.tipp.teamA}
          teamB={ansage.tipp.teamB}
          gewinner={ansage.tipp.gewinner}
          ergebnis={ansage.tipp.ergebnis}
          onFertig={() => void nachDerAuslosung()}
        />
      )}
      <div className="flex max-w-2xl flex-col gap-3">
        {ansage?.title && <h1 className="text-3xl font-semibold">{ansage.title}</h1>}
        <p className="text-lg text-text-muted" aria-live="polite">
          {zustand === 'laedt'
            ? 'Einen Moment …'
            : zustand === 'laedtTon'
              ? 'K4ia holt Luft …'
              : zustand === 'auslosung'
                ? ''
                : zustand === 'ergebnis'
                  ? 'K4ia antippen, dann noch einmal von vorn.'
                  : spricht
                    ? 'K4ia spricht — antippen zum Unterbrechen.'
                    : 'K4ia antippen, dann spricht sie.'}
        </p>
        {ansage && !ansage.sprachausgabe && (
          <p className="text-sm text-status-error">
            Sprachausgabe ist hier nicht eingerichtet — K4ia bleibt stumm.
          </p>
        )}
        {hinweis && (
          <p role="alert" className="text-sm text-status-error">
            {hinweis}
          </p>
        )}
      </div>
    </main>
  )
}
